import { underscore } from '@ember/string';
import type { AdapterPayload } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationAdapter from 'lumen/adapters/application';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  addMockProfile,
  deleteMockProfile,
  mockCostProfiles,
  updateMockProfile,
  USE_MOCK,
} from 'lumen/utils/net-revenue/cost/cost-profiles-mock';
import type {
  ProfileWritePayload,
  RawProfile,
} from 'lumen/utils/net-revenue/cost/types';
import { Promise as RsvpPromise } from 'rsvp';

// TODO: confirm the real path once the BE contract lands.
const PROFILES_BASE = 'cost-profiles';

export default class CostSettingProfileAdapter extends ApplicationAdapter {
  namespace = APIGATEWAY;

  createRecord(
    store: Store,
    type: ModelSchema,
    snapshot: Snapshot,
  ): Promise<AdapterPayload> | RsvpPromise<{ profile: RawProfile }> {
    if (USE_MOCK) {
      return RsvpPromise.resolve({
        profile: addMockProfile(snapshot.serialize() as ProfileWritePayload),
      });
    }

    return super.createRecord(store, type, snapshot);
  }

  deleteRecord(
    store: Store,
    type: ModelSchema,
    snapshot: Snapshot,
  ): Promise<AdapterPayload> | RsvpPromise<Record<string, never>> {
    if (USE_MOCK) {
      deleteMockProfile(snapshot.id as string);

      return RsvpPromise.resolve({});
    }

    return super.deleteRecord(store, type, snapshot);
  }

  // Prevent 'cost-setting/profile' → 'cost-setting/profiles' pluralisation in handleQuery
  pathForType(): string {
    return PROFILES_BASE;
  }

  query(
    store: Store,
    type: ModelSchema,
    query: Record<string, unknown>,
  ):
    | RsvpPromise<{ profiles: RawProfile[] }>
    | ReturnType<ApplicationAdapter['handleQuery']> {
    if (USE_MOCK) {
      return RsvpPromise.resolve<{ profiles: RawProfile[] }>(
        mockCostProfiles(),
      );
    }

    return this.handleQuery(store, type, { ...query, namespace: APIGATEWAY });
  }

  updateRecord(
    store: Store,
    type: ModelSchema,
    snapshot: Snapshot,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): RsvpPromise<any> {
    if (USE_MOCK) {
      const serialized = snapshot.serialize({ includeId: true }) as Record<
        string,
        unknown
      >;
      // `costs` is a relationship, so `changedAttributes()` misses it — without
      // forcing it in, an edit would silently drop the commission.
      const keys = new Set([
        ...Object.keys(snapshot.changedAttributes()),
        'costs',
      ]);

      const body: Record<string, unknown> = {};
      for (const key of keys) {
        const apiKey = underscore(key);
        body[apiKey] = serialized[apiKey];
      }

      return RsvpPromise.resolve({
        profile: updateMockProfile(
          snapshot.id as string,
          body as Partial<ProfileWritePayload>,
        ),
      });
    }

    // TODO: once the real endpoint lands, also force `costs` into the body
    // for the same reason as the mock branch above.
    snapshot.adapterOptions = { ...snapshot.adapterOptions, usePatch: true };

    return super.updateRecord(store, type, snapshot);
  }

  urlForCreateRecord(): string {
    return `${this.host}/${APIGATEWAY}/${PROFILES_BASE}`;
  }

  urlForDeleteRecord(id: string): string {
    return `${this.host}/${APIGATEWAY}/${PROFILES_BASE}/${id}`;
  }

  urlForUpdateRecord(id: string): string {
    return `${this.host}/${APIGATEWAY}/${PROFILES_BASE}/${id}`;
  }
}
